/**
 * New Deal Wizard (`/deals/new`).
 *
 * Phase 3 buyer flow — a four-step wizard that ends with a single
 * `deals.createInvite` call followed by a share step. The buyer no
 * longer has to know the seller's Telegram numeric ID; instead, the
 * backend mints a pending invite in Redis and returns a
 * `t.me/<bot>?startapp=invite_<token>` deep link that the buyer hands
 * off to the seller via Telegram's native share sheet.
 *
 * Step ordering
 * ─────────────
 *  1. Amount  — TON value (we convert to nanotons via `tonToNano` only at
 *     submit time so the user can keep editing without rounding).
 *  2. Details — title, category, description, optional custom terms,
 *     funding timeout (1-168h, default 72).
 *  3. Confirm — summary card + danger-tinted disclaimer + create invite
 *     CTA. On success we flip `shareState` to `ready` and advance to the
 *     share step.
 *  4. Share   — celebration card + "Share via Telegram" button that calls
 *     `WebApp.openTelegramLink('https://t.me/share/url?url=…&text=…')`,
 *     plus a copy-link affordance and a "Done" link to `/deals`.
 *
 * MainButton wiring
 * ─────────────────
 * Steps 1-2 use "Continue" with a `disabled` flag derived from
 * `isStepValid`. Step 3 (Confirm) swaps to "Create Invite" and shows the
 * spinner via `loading: true` while the network call is in flight. Step
 * 4 (Share) shows "Done" and navigates to `/deals` on click.
 *
 * BackButton wiring
 * ─────────────────
 * `AppShell` already binds the Telegram BackButton to `navigate(-1)` for
 * every non-root route. We override that with a local `useBackButton`
 * inside the wizard so the back gesture moves between steps first and
 * only exits to `/deals` from step 0. Once the share step is reached the
 * back gesture returns directly to `/deals` — the user cannot undo an
 * already-created invite by going back one step.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Share2, Copy, Check } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { deals as dealsApi, isApiError } from '@/lib/api';
import type {
  CreateInviteBody,
  Currency,
  DealCategory,
  PendingInvite,
} from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { tonToNano } from '@/lib/ton/format';
import { formatRelativeTime } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { useBackButton, useMainButton, webApp } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/routes/router';

// ─── Types & constants ──────────────────────────────────────

type StepIndex = 0 | 1 | 2 | 3;
const STEP_COUNT = 4;
const TOTAL_STEPS_LITERAL = '4';

/**
 * Phase 3 ships TON-only because USDT requires jetton support in the
 * escrow contract (lands in Phase 4). The selector still renders the
 * disabled USDT pill so users notice the upcoming option.
 */
const SUPPORTED_CURRENCIES: readonly Currency[] = ['TON'] as const;

/**
 * Mock USD price for the TON equivalent helper. There is no price feed in
 * Phase 3 so we hard-code 1 TON ≈ $1 (Phase 4 will pull from CoinGecko).
 */
const MOCK_TON_USD = 1;

const QUICK_PICK_AMOUNTS: readonly string[] = ['1', '5', '10', '50', '100'];

const CATEGORIES: readonly DealCategory[] = [
  'freelance',
  'otc',
  'nft',
  'service',
  'goods',
  'other',
] as const;

const TON_AMOUNT_PATTERN = /^\d+(\.\d{1,9})?$/;

const MIN_TIMEOUT_HOURS = 1;
const MAX_TIMEOUT_HOURS = 168;
const DEFAULT_TIMEOUT_HOURS = 72;
const MAX_DESCRIPTION = 2000;
const MAX_TERMS = 5000;

/** How long the copy-link flash stays on screen. */
const COPY_FLASH_MS = 1800;

interface DraftDeal {
  amount: string; // TON, raw user input
  currency: Currency;
  title: string;
  category: DealCategory;
  description: string;
  terms: string;
  showTerms: boolean;
  timeoutHours: number;
}

const INITIAL_DRAFT: DraftDeal = {
  amount: '',
  currency: 'TON',
  title: '',
  category: 'freelance',
  description: '',
  terms: '',
  showTerms: false,
  timeoutHours: DEFAULT_TIMEOUT_HOURS,
};

type ShareState =
  | { kind: 'idle' }
  | { kind: 'ready'; invite: PendingInvite; shareLink: string };

// ─── Validators ─────────────────────────────────────────────

function isValidAmount(raw: string): boolean {
  if (!TON_AMOUNT_PATTERN.test(raw.trim())) return false;
  try {
    return tonToNano(raw.trim()) > 0n;
  } catch {
    return false;
  }
}

function areDetailsValid(draft: DraftDeal): boolean {
  return (
    draft.title.trim().length >= 3 &&
    draft.title.trim().length <= 200 &&
    draft.description.trim().length >= 10 &&
    draft.description.trim().length <= MAX_DESCRIPTION &&
    (draft.terms.length === 0 || draft.terms.length <= MAX_TERMS) &&
    draft.timeoutHours >= MIN_TIMEOUT_HOURS &&
    draft.timeoutHours <= MAX_TIMEOUT_HOURS
  );
}

function isStepValid(step: StepIndex, draft: DraftDeal): boolean {
  switch (step) {
    case 0:
      return isValidAmount(draft.amount);
    case 1:
      return areDetailsValid(draft);
    case 2:
      return isValidAmount(draft.amount) && areDetailsValid(draft);
    case 3:
      // The share step is always "valid" — the invite has been created by
      // the time we get here and the user just needs to share it.
      return true;
    default:
      return false;
  }
}

// ─── Helpers ────────────────────────────────────────────────

/** Format hours into a friendly "Xd Yh" / "Xd" / "Yh" string. */
function formatTimeoutLabel(
  hours: number,
  t: ReturnType<typeof useT>,
): string {
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  if (days > 0 && rem === 0) {
    return t('newDeal.step.details.timeoutDays', { days });
  }
  if (days === 0) {
    return t('newDeal.step.details.timeoutHours', { hours });
  }
  return t('newDeal.step.details.timeoutCombined', { days, hours: rem });
}

/** Format the mocked USD equivalent. */
function formatUsdEquivalent(amount: string): string | null {
  if (!isValidAmount(amount)) return null;
  // Safe: amount has been validated to a `\d+(\.\d{1,9})?` pattern. We
  // intentionally use `Number` here only for the cosmetic USD label
  // because no money math is performed on the result. The real on-chain
  // amount path stays bigint via `tonToNano`.
  const ton = Number(amount.trim());
  const usd = ton * MOCK_TON_USD;
  return usd.toFixed(2);
}

// ─── Step indicator ─────────────────────────────────────────

interface StepIndicatorProps {
  current: StepIndex;
}

function StepIndicator({ current }: StepIndicatorProps): JSX.Element {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={STEP_COUNT}
      aria-valuenow={current + 1}
      className="flex items-center justify-center gap-2"
    >
      {Array.from({ length: STEP_COUNT }).map((_, idx) => (
        <span
          key={idx}
          aria-hidden
          className={cn(
            'h-2 rounded-full transition-all duration-300',
            idx === current
              ? 'w-6 bg-accent-gold shadow-glow-gold'
              : idx < current
                ? 'w-2 bg-accent-purple/70'
                : 'w-2 bg-border',
          )}
        />
      ))}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

interface QuickPickProps {
  values: readonly string[];
  active: string;
  onPick: (value: string) => void;
}

function QuickPick({ values, active, onPick }: QuickPickProps): JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value) => {
        const isActive = active === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => {
              haptics.selectionChanged();
              onPick(value);
            }}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
              'focus-visible:ring-offset-bg-primary',
              isActive
                ? 'border-accent-gold/80 bg-accent-gold/15 text-accent-gold'
                : 'border-border bg-bg-secondary/50 text-text-secondary hover:text-text-primary',
            )}
          >
            {value} TON
          </button>
        );
      })}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────

export function NewDealWizardPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();

  const [step, setStep] = useState<StepIndex>(0);
  const [draft, setDraft] = useState<DraftDeal>(INITIAL_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shareState, setShareState] = useState<ShareState>({ kind: 'idle' });
  const [copyFlash, setCopyFlash] = useState(false);

  // Track the latest createInvite call so a re-mount during HMR (or a
  // user navigating away mid-submit) does not trigger ghost navigation.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // ── Navigation ──────────────────────────────────────────
  const goBack = useCallback((): void => {
    haptics.light();
    // Once the invite exists and we are on the share step, the back
    // gesture exits the wizard entirely — the user cannot "undo" an
    // already-created invite by stepping backwards.
    if (step === 0 || shareState.kind === 'ready') {
      navigate(ROUTES.deals);
      return;
    }
    setStep((prev) => (prev - 1) as StepIndex);
  }, [navigate, shareState.kind, step]);

  const goNext = useCallback((): void => {
    if (!isStepValid(step, draft)) return;
    haptics.light();
    if (step < STEP_COUNT - 1) {
      setStep((prev) => (prev + 1) as StepIndex);
    }
  }, [draft, step]);

  // BackButton — overrides AppShell's default `navigate(-1)` so the back
  // gesture walks the wizard rather than leaving the page.
  useBackButton(goBack);

  // ── Submission ──────────────────────────────────────────
  const submit = useCallback(async (): Promise<void> => {
    if (submitting) return;
    if (!isStepValid(2, draft)) return;

    haptics.medium();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const nano = tonToNano(draft.amount.trim());

      const body: CreateInviteBody = {
        amount: nano.toString(),
        title: draft.title.trim(),
        description: draft.description.trim(),
        category: draft.category,
        currency: draft.currency,
        timeoutHours: draft.timeoutHours,
        ...(draft.terms.trim().length > 0 ? { terms: draft.terms.trim() } : {}),
      };

      const response = await dealsApi.createInvite(body);
      if (cancelledRef.current) return;

      haptics.success();
      setShareState({
        kind: 'ready',
        invite: response.invite,
        shareLink: response.shareLink,
      });
      setStep(3);
    } catch (err) {
      if (cancelledRef.current) return;
      haptics.error();
      const message = isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : t('newDeal.error.createFailed');
      setSubmitError(message);
    } finally {
      if (!cancelledRef.current) {
        setSubmitting(false);
      }
    }
  }, [draft, submitting, t]);

  // ── Share step actions ──────────────────────────────────
  const handleShare = useCallback((): void => {
    if (shareState.kind !== 'ready') return;
    haptics.medium();

    // Build the human-friendly invite message. We use the raw TON amount
    // the user typed (not the nano-formatted bigint) so the seller sees
    // "5 TON" instead of "5000000000 nanotons".
    const inviteText = t('newDeal.step.share.inviteMessage', {
      amount: draft.amount,
      title: draft.title.trim(),
      link: shareState.shareLink,
    });

    const shareUrl =
      `https://t.me/share/url?url=${encodeURIComponent(shareState.shareLink)}` +
      `&text=${encodeURIComponent(inviteText)}`;

    const tg = webApp();
    if (tg && typeof tg.openTelegramLink === 'function') {
      tg.openTelegramLink(shareUrl);
      return;
    }

    // Dev-browser fallback — open the share URL in a new tab so we can at
    // least see the payload when running outside Telegram.
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }, [draft.amount, draft.title, shareState, t]);

  const handleCopy = useCallback(async (): Promise<void> => {
    if (shareState.kind !== 'ready') return;
    try {
      await navigator.clipboard.writeText(shareState.shareLink);
      haptics.success();
      setCopyFlash(true);
      window.setTimeout(() => {
        if (!cancelledRef.current) setCopyFlash(false);
      }, COPY_FLASH_MS);
    } catch {
      haptics.error();
    }
  }, [shareState]);

  const handleDone = useCallback((): void => {
    haptics.light();
    navigate(ROUTES.deals);
  }, [navigate]);

  // ── Telegram MainButton ─────────────────────────────────
  const mainButtonConfig = useMemo(() => {
    if (step === 3) {
      return {
        text: t('newDeal.button.doneShare'),
        onClick: handleDone,
        disabled: false,
        loading: false,
      };
    }

    const isConfirmStep = step === 2;
    const text = isConfirmStep
      ? submitting
        ? t('newDeal.button.creatingInvite')
        : t('newDeal.button.createInvite')
      : t('newDeal.button.continue');

    return {
      text,
      onClick: isConfirmStep ? () => void submit() : goNext,
      disabled: !isStepValid(step, draft) || submitting,
      loading: submitting,
    };
  }, [draft, goNext, handleDone, step, submit, submitting, t]);

  useMainButton(mainButtonConfig);

  // ── Field setters ───────────────────────────────────────
  const setField = useCallback(
    <K extends keyof DraftDeal>(key: K, value: DraftDeal[K]): void => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleAmountChange = (value: string): void => {
    // Allow free typing (we validate on submit). Strip everything except
    // digits and a single decimal point so the keyboard cannot inject
    // letters even when the user pastes garbage.
    const sanitised = value.replace(/[^0-9.]/g, '');
    const parts = sanitised.split('.');
    const cleaned =
      parts.length <= 1
        ? sanitised
        : `${parts[0]}.${parts.slice(1).join('').slice(0, 9)}`;
    setField('amount', cleaned);
  };

  // ── Step renderers ──────────────────────────────────────
  const renderAmountStep = (): JSX.Element => {
    const usd = formatUsdEquivalent(draft.amount);
    const showAmountError =
      draft.amount.length > 0 && !isValidAmount(draft.amount);

    return (
      <Card className="flex flex-col gap-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('newDeal.step.amount.title')}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {t('newDeal.step.amount.helper')}
          </p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Input
            inputMode="decimal"
            autoComplete="off"
            placeholder={t('newDeal.step.amount.placeholder')}
            value={draft.amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            aria-label={t('newDeal.step.amount.title')}
            className={cn(
              'h-16 border-2 bg-transparent text-center text-3xl font-bold tracking-tight',
              showAmountError && 'border-danger/70',
            )}
          />
          <span className="text-xs text-text-muted">
            {usd
              ? t('newDeal.step.amount.usdEquivalent', { amount: usd })
              : t('newDeal.step.amount.usdEquivalent', { amount: '0.00' })}
          </span>
          {showAmountError && (
            <p className="text-xs text-danger">
              {t('newDeal.error.invalidAmount')}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label>{t('newDeal.step.amount.quickPick')}</Label>
          <QuickPick
            values={QUICK_PICK_AMOUNTS}
            active={draft.amount}
            onPick={(value) => setField('amount', value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>{t('newDeal.step.amount.currencyLabel')}</Label>
          <div className="flex gap-2">
            {SUPPORTED_CURRENCIES.map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => {
                  haptics.selectionChanged();
                  setField('currency', currency);
                }}
                className={cn(
                  'rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors',
                  draft.currency === currency
                    ? 'border-accent-gold/80 bg-accent-gold/15 text-accent-gold'
                    : 'border-border bg-bg-secondary/50 text-text-secondary',
                )}
              >
                {t('newDeal.step.amount.currencyTon')}
              </button>
            ))}
            <button
              type="button"
              disabled
              aria-disabled
              className="cursor-not-allowed rounded-full border border-border bg-bg-secondary/30 px-4 py-1.5 text-xs font-semibold text-text-muted opacity-60"
              title={t('newDeal.step.amount.currencyUsdtSoon')}
            >
              {t('newDeal.step.amount.currencyUsdt')}
            </button>
          </div>
          <p className="text-[10px] text-text-muted">
            {t('newDeal.step.amount.currencyUsdtSoon')}
          </p>
        </div>
      </Card>
    );
  };

  const renderDetailsStep = (): JSX.Element => {
    const titleTooShort =
      draft.title.length > 0 && draft.title.trim().length < 3;
    const descriptionTooShort =
      draft.description.length > 0 && draft.description.trim().length < 10;

    return (
      <Card className="flex flex-col gap-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('newDeal.step.details.title')}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {t('newDeal.step.details.helper')}
          </p>
        </div>

        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="deal-title">
            {t('newDeal.step.details.titleLabel')}
          </Label>
          <Input
            id="deal-title"
            value={draft.title}
            maxLength={200}
            placeholder={t('newDeal.step.details.titlePlaceholder')}
            onChange={(e) => setField('title', e.target.value)}
            className={cn(titleTooShort && 'border-danger/70')}
          />
          {titleTooShort && (
            <p className="text-xs text-danger">
              {t('newDeal.error.titleTooShort')}
            </p>
          )}
        </div>

        {/* Category */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="deal-category">
            {t('newDeal.step.details.categoryLabel')}
          </Label>
          <select
            id="deal-category"
            value={draft.category}
            onChange={(e) =>
              setField('category', e.target.value as DealCategory)
            }
            className={cn(
              'h-10 w-full rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
            )}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {t(`newDeal.category.${cat}` as const)}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="deal-description">
              {t('newDeal.step.details.descriptionLabel')}
            </Label>
            <span className="text-[10px] text-text-muted">
              {t('newDeal.step.details.descriptionCount', {
                count: draft.description.length,
                max: MAX_DESCRIPTION,
              })}
            </span>
          </div>
          <textarea
            id="deal-description"
            value={draft.description}
            maxLength={MAX_DESCRIPTION}
            rows={5}
            placeholder={t('newDeal.step.details.descriptionPlaceholder')}
            onChange={(e) => setField('description', e.target.value)}
            className={cn(
              'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
              descriptionTooShort && 'border-danger/70',
            )}
          />
          {descriptionTooShort && (
            <p className="text-xs text-danger">
              {t('newDeal.error.descriptionTooShort')}
            </p>
          )}
        </div>

        {/* Optional terms */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              haptics.selectionChanged();
              setField('showTerms', !draft.showTerms);
            }}
            className="self-start text-xs font-medium text-accent-purple underline-offset-4 hover:underline"
          >
            {draft.showTerms
              ? t('newDeal.step.details.hideTerms')
              : t('newDeal.step.details.addTerms')}
          </button>
          {draft.showTerms && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="deal-terms">
                {t('newDeal.step.details.termsLabel')}
              </Label>
              <textarea
                id="deal-terms"
                value={draft.terms}
                maxLength={MAX_TERMS}
                rows={4}
                placeholder={t('newDeal.step.details.termsPlaceholder')}
                onChange={(e) => setField('terms', e.target.value)}
                className={cn(
                  'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                )}
              />
              <span className="self-end text-[10px] text-text-muted">
                {t('newDeal.step.details.descriptionCount', {
                  count: draft.terms.length,
                  max: MAX_TERMS,
                })}
              </span>
            </div>
          )}
        </div>

        {/* Timeout slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="deal-timeout">
              {t('newDeal.step.details.timeoutLabel')}
            </Label>
            <span className="text-xs font-semibold text-accent-gold">
              {formatTimeoutLabel(draft.timeoutHours, t)}
            </span>
          </div>
          <input
            id="deal-timeout"
            type="range"
            min={MIN_TIMEOUT_HOURS}
            max={MAX_TIMEOUT_HOURS}
            step={1}
            value={draft.timeoutHours}
            onChange={(e) =>
              setField('timeoutHours', Number.parseInt(e.target.value, 10))
            }
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-bg-tertiary accent-accent-gold"
          />
        </div>
      </Card>
    );
  };

  const renderConfirmStep = (): JSX.Element => {
    const usd = formatUsdEquivalent(draft.amount);

    return (
      <div className="flex flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {t('newDeal.step.confirm.title')}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {t('newDeal.step.confirm.helper')}
            </p>
          </div>

          {/* Big amount */}
          <div className="flex flex-col items-center gap-1 rounded-md border border-border/40 bg-bg-secondary/30 py-4">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {t('newDeal.step.confirm.summaryAmount')}
            </span>
            <span className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-3xl font-bold text-transparent">
              {draft.amount || '0'} TON
            </span>
            {usd && (
              <span className="text-xs text-text-muted">
                {t('newDeal.step.amount.usdEquivalent', { amount: usd })}
              </span>
            )}
          </div>

          {/* Summary rows — no Seller row; the seller is unknown until
              they accept the invite. */}
          <dl className="flex flex-col gap-2 text-sm">
            <SummaryRow
              label={t('newDeal.step.confirm.summaryTitle')}
              value={draft.title || '—'}
            />
            <SummaryRow
              label={t('newDeal.step.confirm.summaryCategory')}
              value={t(`newDeal.category.${draft.category}` as const)}
            />
            <SummaryRow
              label={t('newDeal.step.confirm.summaryDescription')}
              value={
                draft.description.length > 120
                  ? `${draft.description.slice(0, 120)}…`
                  : draft.description || '—'
              }
              multiline
            />
            {draft.terms.trim().length > 0 && (
              <SummaryRow
                label={t('newDeal.step.confirm.summaryTerms')}
                value={
                  draft.terms.length > 120
                    ? `${draft.terms.slice(0, 120)}…`
                    : draft.terms
                }
                multiline
              />
            )}
            <SummaryRow
              label={t('newDeal.step.confirm.summaryTimeout')}
              value={formatTimeoutLabel(draft.timeoutHours, t)}
            />
          </dl>

          <p className="text-xs leading-relaxed text-text-secondary">
            {t('newDeal.step.confirm.noSellerYet')}
          </p>
        </Card>

        {/* Disclaimer */}
        <Card
          className="flex items-start gap-3 border-danger/40 bg-danger/10"
          role="alert"
        >
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-danger"
            aria-hidden
          />
          <p className="text-xs leading-relaxed text-text-secondary">
            {t('newDeal.step.confirm.disclaimer')}
          </p>
        </Card>

        {/* Inline submit error */}
        {submitError !== null && (
          <Card className="border-danger/60 bg-danger/10" role="alert">
            <p className="text-sm font-semibold text-text-primary">
              {t('newDeal.error.createFailed')}
            </p>
            <p className="mt-1 text-xs text-text-secondary">{submitError}</p>
          </Card>
        )}
      </div>
    );
  };

  const renderShareStep = (): JSX.Element => {
    if (shareState.kind !== 'ready') {
      // Placeholder — the share step only mounts visibly after a
      // successful createInvite, so in practice this branch is never
      // rendered. Still, we provide a friendly fallback so the sliding
      // viewport has something to show during the transition.
      return (
        <Card className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-text-primary">
            {t('newDeal.step.share.title')}
          </h2>
          <p className="text-sm text-text-secondary">
            {t('newDeal.step.share.subtitle')}
          </p>
        </Card>
      );
    }

    const { invite, shareLink } = shareState;
    const expiresLabel = formatRelativeTime(invite.expiresAt);

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-2xl font-bold text-text-primary">
            {t('newDeal.step.share.title')}
          </h2>
          <p className="max-w-md text-sm text-text-secondary">
            {t('newDeal.step.share.subtitle')}
          </p>
        </div>

        {/* Glassmorphism summary card */}
        <Card
          className={cn(
            'flex flex-col gap-3 border-accent-gold/30',
            'bg-gradient-to-br from-accent-gold/10 via-bg-secondary/40 to-accent-purple/10',
            'shadow-glow-gold backdrop-blur-sm',
          )}
        >
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {t('newDeal.step.confirm.summaryTitle')}
            </span>
            <span className="text-base font-semibold text-text-primary">
              {invite.title}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {t('newDeal.step.share.cardAmount')}
            </span>
            <span className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-2xl font-bold text-transparent">
              {draft.amount} TON
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {t('newDeal.step.share.cardExpires')}
            </span>
            <span className="text-sm text-text-secondary">{expiresLabel}</span>
          </div>
        </Card>

        {/* Share via Telegram — primary action */}
        <button
          type="button"
          onClick={handleShare}
          className={cn(
            'flex h-12 w-full items-center justify-center gap-2 rounded-xl',
            'bg-gradient-to-r from-accent-gold to-accent-gold/80 text-bg-primary',
            'text-sm font-semibold shadow-glow-gold transition-transform',
            'hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
            'focus-visible:ring-offset-bg-primary',
          )}
        >
          <Share2 className="h-4 w-4" aria-hidden />
          {t('newDeal.step.share.shareButton')}
        </button>

        {/* Copy link — secondary action */}
        <button
          type="button"
          onClick={() => void handleCopy()}
          className={cn(
            'flex h-11 w-full items-center justify-center gap-2 rounded-xl',
            'border border-border bg-bg-secondary/50 text-sm font-medium text-text-primary',
            'transition-colors hover:bg-bg-secondary',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
            'focus-visible:ring-offset-bg-primary',
          )}
          aria-live="polite"
        >
          {copyFlash ? (
            <>
              <Check className="h-4 w-4 text-success" aria-hidden />
              {t('newDeal.step.share.copied')}
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-hidden />
              {t('newDeal.step.share.copyButton')}
            </>
          )}
        </button>

        {/* Raw link for power users + screen readers. */}
        <p
          className="break-all rounded-md border border-border/50 bg-bg-secondary/30 p-2 text-[11px] font-mono text-text-muted"
          aria-label={shareLink}
        >
          {shareLink}
        </p>

        {/* Footer + Done link */}
        <div className="flex flex-col items-center gap-1 pt-2">
          <p className="text-[11px] text-text-muted">
            {t('newDeal.step.share.footer')}
          </p>
          <button
            type="button"
            onClick={handleDone}
            className="text-xs font-medium text-accent-purple underline-offset-4 hover:underline"
          >
            {t('newDeal.step.share.done')}
          </button>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────
  const slides: readonly JSX.Element[] = [
    renderAmountStep(),
    renderDetailsStep(),
    renderConfirmStep(),
    renderShareStep(),
  ];

  return (
    <AppShell>
      <PageHeader title={t('newDeal.title')} />

      <StepIndicator current={step} />
      <p className="-mt-4 text-center text-[10px] uppercase tracking-wide text-text-muted">
        {t('newDeal.stepIndicator', {
          current: step + 1,
          total: TOTAL_STEPS_LITERAL,
        })}
      </p>

      {/* Sliding viewport. Each slide is full width; we translate the
          inner track horizontally based on `step`. `overflow-hidden` on
          the viewport keeps non-active slides off-screen. */}
      <div className="relative -mx-4 overflow-hidden">
        <div
          className="flex w-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${step * 100}%)` }}
        >
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className="w-full shrink-0 px-4"
              aria-hidden={idx !== step}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

// ─── Summary row ────────────────────────────────────────────

interface SummaryRowProps {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
}

function SummaryRow({
  label,
  value,
  mono,
  multiline,
}: SummaryRowProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex gap-3 border-b border-border/30 pb-2 last:border-0 last:pb-0',
        multiline ? 'flex-col' : 'items-center justify-between',
      )}
    >
      <dt className="text-[10px] uppercase tracking-wide text-text-muted">
        {label}
      </dt>
      <dd
        className={cn(
          'text-sm text-text-primary',
          mono && 'font-mono',
          multiline && 'whitespace-pre-wrap',
          !multiline && 'text-right',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
